import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Popover2 } from '@evertrue/et-components';

const DATE_OPERATORS = [
  { value: 'all', label: 'All Time' },
  { value: 'last_7', label: 'Last 7 Days' },
  { value: 'last_30', label: 'Last 30 Days' },
  { value: 'last_90', label: 'Last 90 Days' },
  { value: 'this_year', label: 'This Year' },
  { value: 'is_before', label: 'Is before' },
  { value: 'is_after', label: 'Is after' },
  { value: 'is_between', label: 'Is between' },
  { value: 'is_not_between', label: 'Is not between' },
  { value: 'is_on', label: 'Is on' },
  { value: 'is_not_on', label: 'Is not on' },
];

const OPERATORS_WITH_SINGLE_DATE = ['is_before', 'is_after', 'is_on', 'is_not_on'];
const OPERATORS_WITH_DATE_RANGE = ['is_between', 'is_not_between'];

/**
 * Date filter popover with preset shortcuts and advanced operators.
 * Emits a filter config { operator, date1, date2 } that the parent uses for filtering.
 */
const DateFilterPopover = ({ value, onChange, label = 'Created' }) => {
  const [, setIsOpen] = useState(false);

  const displayLabel = () => {
    const op = DATE_OPERATORS.find((o) => o.value === value.operator);
    if (!op || value.operator === 'all') return label;
    if (OPERATORS_WITH_SINGLE_DATE.includes(value.operator) && value.date1) {
      return `${label} | ${op.label} ${value.date1}`;
    }
    if (OPERATORS_WITH_DATE_RANGE.includes(value.operator) && value.date1 && value.date2) {
      return `${label} | ${op.label} ${value.date1} – ${value.date2}`;
    }
    return `${label} | ${op.label}`;
  };

  const handleOperatorChange = (operator) => {
    if (['all', 'last_7', 'last_30', 'last_90', 'this_year'].includes(operator)) {
      onChange({ operator, date1: null, date2: null });
      setIsOpen(false);
    } else {
      onChange({ ...value, operator });
    }
  };

  const handleApply = () => {
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange({ operator: 'all', date1: null, date2: null });
    setIsOpen(false);
  };

  const needsDateInput =
    OPERATORS_WITH_SINGLE_DATE.includes(value.operator) || OPERATORS_WITH_DATE_RANGE.includes(value.operator);
  const needsSecondDate = OPERATORS_WITH_DATE_RANGE.includes(value.operator);
  const isActive = value.operator !== 'all';

  return (
    <Popover2
      trigger={
        <div role="button" tabIndex={0} className={`cadences-v2-filters--btn ${isActive ? 'is-active' : ''}`}>
          <span>{displayLabel()}</span>
          <Icon icon="chevron-down" size={1} />
        </div>
      }
      contentClassName="cadences-v2-date-filter"
      onToggle={setIsOpen}
    >
      {() => (
        <div className="cadences-v2-date-filter--menu">
          <div className="cadences-v2-date-filter--header">Filter by Date</div>
          <div className="cadences-v2-date-filter--operators">
            {DATE_OPERATORS.map((op) => (
              <button
                key={op.value}
                type="button"
                className={`cadences-v2-date-filter--op ${value.operator === op.value ? 'is-selected' : ''}`}
                onClick={() => handleOperatorChange(op.value)}
              >
                {op.label}
              </button>
            ))}
          </div>
          {needsDateInput && (
            <div className="cadences-v2-date-filter--inputs">
              <input
                type="date"
                className="cadences-v2-date-filter--date-input"
                value={value.date1 || ''}
                onChange={(e) => onChange({ ...value, date1: e.target.value })}
              />
              {needsSecondDate && (
                <>
                  <span className="cadences-v2-date-filter--separator">to</span>
                  <input
                    type="date"
                    className="cadences-v2-date-filter--date-input"
                    value={value.date2 || ''}
                    onChange={(e) => onChange({ ...value, date2: e.target.value })}
                  />
                </>
              )}
              <Button type="action" size="small" onClick={handleApply}>
                Apply
              </Button>
            </div>
          )}
          {isActive && (
            <button type="button" className="cadences-v2-date-filter--clear" onClick={handleClear}>
              Clear Filter
            </button>
          )}
        </div>
      )}
    </Popover2>
  );
};

/**
 * Parse a date-only string (YYYY-MM-DD) as local midnight.
 * new Date("2026-03-12") treats date-only strings as UTC, which causes
 * timezone-offset mismatches when comparing against local dates.
 */
const parseLocalDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

/**
 * Apply a date filter config to a date string.
 * @param {string} dateString - ISO date string to test
 * @param {{ operator: string, date1: string|null, date2: string|null }} filter
 * @returns {boolean} true if the date passes the filter
 */
export const matchesDateFilter = (dateString, filter) => {
  if (!filter || filter.operator === 'all') return true;
  if (!dateString) return false;
  // Handle unix seconds (e.g. "1772831315.000000000" from outreach API)
  let created;
  const num = typeof dateString === 'string' ? parseFloat(dateString) : dateString;
  if (typeof num === 'number' && !isNaN(num) && num > 1e9 && num < 1e11) {
    created = new Date(num * 1000);
  } else if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    // Date-only strings: parse as local midnight (not UTC)
    created = parseLocalDate(dateString);
  } else {
    created = new Date(dateString);
  }
  if (isNaN(created.getTime())) return false;

  const now = new Date();
  // Use UTC date for comparison so a UTC timestamp on Mar 27 01:57
  // isn't shifted to Mar 26 in local timezone
  const startOfDay = (d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));

  switch (filter.operator) {
    case 'last_7':
      return created >= new Date(now.getTime() - 7 * 86400000);
    case 'last_30':
      return created >= new Date(now.getTime() - 30 * 86400000);
    case 'last_90':
      return created >= new Date(now.getTime() - 90 * 86400000);
    case 'this_year':
      return created.getFullYear() === now.getFullYear();
    case 'is_before':
      return filter.date1 ? startOfDay(created) < startOfDay(parseLocalDate(filter.date1)) : true;
    case 'is_after':
      return filter.date1 ? startOfDay(created) > startOfDay(parseLocalDate(filter.date1)) : true;
    case 'is_on':
      return filter.date1 ? startOfDay(created).getTime() === startOfDay(parseLocalDate(filter.date1)).getTime() : true;
    case 'is_not_on':
      return filter.date1 ? startOfDay(created).getTime() !== startOfDay(parseLocalDate(filter.date1)).getTime() : true;
    case 'is_between':
      if (!filter.date1 || !filter.date2) return true;
      return (
        startOfDay(created) >= startOfDay(parseLocalDate(filter.date1)) &&
        startOfDay(created) <= startOfDay(parseLocalDate(filter.date2))
      );
    case 'is_not_between':
      if (!filter.date1 || !filter.date2) return true;
      return (
        startOfDay(created) < startOfDay(parseLocalDate(filter.date1)) ||
        startOfDay(created) > startOfDay(parseLocalDate(filter.date2))
      );
    default:
      return true;
  }
};

DateFilterPopover.propTypes = {
  value: PropTypes.shape({
    operator: PropTypes.string.isRequired,
    date1: PropTypes.string,
    date2: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
};

export default DateFilterPopover;
